import type { IntermediateFormat } from '../../../domains/global-filters/model/IntermediateFormat';
import type {
  QueryFormatFilter,
  UserAttributeQueryKind,
} from '../../../domains/global-filters/model/QueryFormat';
import {
  QueryMapOperandEnum,
  QueryKeyEnum,
} from '../../../domains/global-filters/model/QueryFormat';
import * as RadioDateConverter from './GenericConverter/RadioDateConverter';
import * as RadioNumberConverter from './GenericConverter/RadioNumberConverter';
import * as RadioTextConverter from './GenericConverter/RadioTextConverter';
import * as RadioBooleanConverter from './GenericConverter/RadioBooleanConverter';
import type { UserAttributeType } from '../../../components/StatelessComponent/SingleFilter/UserAttributeFilter/UserAttributeFilterType';
import { UserAttributeKind } from '../../../domains/user-attributes/model/UserAttribute';

const shouldNotHappen = (never: never): void => {
  throw new Error(`Unknown type: ${never}`);
};

const convertToIntermediateKind = (kind: UserAttributeKind): UserAttributeQueryKind => {
  switch (kind) {
    case UserAttributeKind.Number: {
      return 'number';
    }
    case UserAttributeKind.String: {
      return 'str';
    }
    case UserAttributeKind.Email: {
      return 'email';
    }
    case UserAttributeKind.Boolean: {
      return 'bool';
    }
    case UserAttributeKind.Date: {
      return 'date';
    }
    case UserAttributeKind.Unknown: {
      return 'unknown';
    }
    default: {
      shouldNotHappen(kind);
    }
  }
  return 'unknown';
};

export function fromUiToQueryFormat(uiValue?: UserAttributeType): QueryFormatFilter {
  if (!uiValue) {
    throw new Error(`No uiValue`);
  }
  if (uiValue.name == null) {
    throw new Error(`No user attribute name`);
  }

  // We force the queryKey type
  const queryKey = `${QueryKeyEnum.user_attributes}.${convertToIntermediateKind(uiValue.kind)}.${
    uiValue.name
  }` as QueryKeyEnum;
  switch (uiValue.kind) {
    case UserAttributeKind.Number: {
      return RadioNumberConverter.fromUiToQueryFormat(uiValue.value, queryKey);
    }
    case UserAttributeKind.String: {
      return RadioTextConverter.fromUiToQueryFormat(uiValue.value, queryKey);
    }
    case UserAttributeKind.Email: {
      return RadioTextConverter.fromUiToQueryFormat(uiValue.value, queryKey);
    }
    case UserAttributeKind.Boolean: {
      return RadioBooleanConverter.fromUiToQueryFormat(uiValue.value, queryKey);
    }
    case UserAttributeKind.Date: {
      return RadioDateConverter.fromUiToQueryFormat(uiValue.value, queryKey);
    }
    case UserAttributeKind.Unknown: {
      // We should not arrive here as we filter out unknown user attributes
      // But to avoid breaking the flow, we return a default value
      return {
        [QueryMapOperandEnum.UNKNOWN]: {
          [queryKey]: true,
        },
      };
    }
    default: {
      shouldNotHappen(uiValue);
    }
  }
  throw new Error(`User attribute kind not supported: ${uiValue}`);
}

export function fromIntermediateToUiFormat(
  intermediateValue: IntermediateFormat,
): UserAttributeType {
  if (intermediateValue.userAttributeKey == null) {
    throw new Error(`User attribute has no userAttributeKey`);
  }

  // userAttributeKey looks like this: "user_attributes.bool.a boolean attribute"
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, userAttributeKindString, userAttributeName] =
    intermediateValue.userAttributeKey.split('.');
  if (userAttributeKindString != null) {
    const userAttributeKind = userAttributeKindString as UserAttributeQueryKind;
    switch (userAttributeKind) {
      case 'number': {
        return {
          kind: UserAttributeKind.Number,
          name: userAttributeName,
          value: RadioNumberConverter.fromIntermediateToUiFormat(intermediateValue),
        };
      }
      case 'str': {
        return {
          kind: UserAttributeKind.String,
          name: userAttributeName,
          value: RadioTextConverter.fromIntermediateToUiFormat(intermediateValue),
        };
      }
      case 'email': {
        return {
          kind: UserAttributeKind.Email,
          name: userAttributeName,
          value: RadioTextConverter.fromIntermediateToUiFormat(intermediateValue),
        };
      }
      case 'bool': {
        return {
          kind: UserAttributeKind.Boolean,
          name: userAttributeName,
          value: RadioBooleanConverter.fromIntermediateToUiFormat(intermediateValue),
        };
      }
      case 'date': {
        return {
          kind: UserAttributeKind.Date,
          name: userAttributeName,
          value: RadioDateConverter.fromIntermediateToUiFormat(intermediateValue),
        };
      }
      case 'unknown': {
        // We should not arrive here as we filter out unknown user attributes
        // But to avoid breaking the flow, we return a default value
        return {
          kind: UserAttributeKind.Unknown,
          name: userAttributeName,
          value: undefined,
        };
      }
      default: {
        shouldNotHappen(userAttributeKind);
      }
    }
  }
  throw new Error(`User attribute has no kind property`);
}
